import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, map, Observable } from 'rxjs';
import { AppEnvironmentConfig } from 'projects/config/model/environment.config.model';
import { AbstractCounterBusStop } from '../counters/abstract-counter-bus-stop';
import { SvcParamItem } from './svc-param-item.model';

export interface SvcSiteParametersResponse {
  countryId: number;
  languageId: number;
  timezoneId: number;
  currencyId: number;
  latitude: number;
  longitude: number;
  categoryIds: number[];
}

@Injectable({
  providedIn: 'root',
})
export class SvcSiteParamService extends AbstractCounterBusStop<{ siteId?: number }, SvcParamItem> {
  private http = inject(HttpClient);
  private siteId: number;

  protected get skipRequestCollector(): boolean {
    return true;
  }

  constructor(private appConfig: AppEnvironmentConfig) {
    super({
      apiURL: appConfig.APIs.apiUrlAdministration,
      singleParamInPath: true,
      idBatchParamName: 'paramKeys',
      registryIdFilterPropName: 'key',
      idSingleRequestPath: (_, ids) => `/Parameter/site/value/cached/${ids[0]}`,
      idBatchRequestPath: () => '/Parameter/site/values/cached',
    });
  }

  public setSiteId(siteId: number): void {
    this.siteId = siteId;
  }

  public getParameterByParamName(paramName: string): Observable<SvcParamItem> {
    return this.getCountByIdOrUid(paramName, this.siteId ? { siteId: this.siteId } : {}).pipe(
      map((r) => {
        if (r.length) {
          return (typeof r[0] === 'object' ? r[0] : { key: paramName, value: r[0] ?? '' }) ?? this.#generateDefaultParamItem(paramName);
        }
        return this.#generateDefaultParamItem(paramName);
      })
    );
  }

  public getParameterByParamsName(paramsName: string[]): Observable<SvcParamItem[]> {
    return forkJoin(paramsName.map((p) => this.getParameterByParamName(p)));
  }

  public getSiteParameters(siteId?: number): Observable<SvcSiteParametersResponse> {
    const params =
      typeof siteId === 'number' && Number.isFinite(siteId) && siteId > 0
        ? new HttpParams().set('siteId', siteId)
        : undefined;
    return this.http.get<SvcSiteParametersResponse>(`${this.apiURL}/Parameter/site`, params ? { params } : undefined);
  }

  #generateDefaultParamItem(paramName: string): SvcParamItem {
    return { key: paramName, value: '' };
  }
}
